import { useState, useCallback, useRef, useMemo } from 'react';
import type { DistributiveOmit } from './useMultiplayer';
import type { ClientMessage } from '@roll1/shared';
import { MAP_BASE_PROMPT } from '@roll1/shared';
import { useI18n } from '../i18n';

// ── Types ────────────────────────────────────────────────────────

/** Region выделения в map-space координатах */
export type MaskRegion =
  | { type: 'rect'; x: number; y: number; width: number; height: number }
  | { type: 'lasso'; points: Array<{ x: number; y: number }> };

/** Клиентское состояние inpainting mode */
export interface InpaintingState {
  tool: 'rect' | 'lasso' | null;
  region: MaskRegion | null;
  prompt: string;
  previewUrl: string | null;
  generating: boolean;
  error: string | null;
}

const INITIAL_STATE: InpaintingState = {
  tool: null,
  region: null,
  prompt: MAP_BASE_PROMPT,
  previewUrl: null,
  generating: false,
  error: null,
};

// ── Mask Export ───────────────────────────────────────────────────

/** Export mask region as PNG blob (black background, white = inpaint area) */
export function exportMaskAsPng(
  region: MaskRegion,
  mapWidth: number,
  mapHeight: number,
  t?: (key: any) => string,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = mapWidth;
    canvas.height = mapHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) { reject(new Error(t?.('hookError.canvasContext') ?? 'Canvas 2d context unavailable')); return; }

    // Fill black (preserve area)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, mapWidth, mapHeight);

    // Draw white (inpaint area)
    ctx.fillStyle = '#ffffff';
    if (region.type === 'rect') {
      ctx.fillRect(region.x, region.y, region.width, region.height);
    } else {
      const pts = region.points;
      if (pts.length < 3) { reject(new Error(t?.('hookError.lassoMinPoints') ?? 'Lasso needs at least 3 points')); return; }
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(pts[i].x, pts[i].y);
      }
      ctx.closePath();
      ctx.fill();
    }

    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error(t?.('hookError.exportFailed') ?? 'Failed to export mask'));
    }, 'image/png');
  });
}

// ── Validation ───────────────────────────────────────────────────

function getMaskArea(region: MaskRegion): number {
  if (region.type === 'rect') {
    return Math.abs(region.width * region.height);
  }
  // Shoelace formula for polygon area
  const pts = region.points;
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    area += pts[i].x * pts[j].y;
    area -= pts[j].x * pts[i].y;
  }
  return Math.abs(area) / 2;
}

// ── Hook ─────────────────────────────────────────────────────────

interface UseInpaintingOptions {
  sessionId: string;
  participantId: string;
  pageId: string;
  mapWidth: number;
  mapHeight: number;
  /** Map scale factor — world coords = pixel coords × mapScale */
  mapScale?: number;
  send: (msg: DistributiveOmit<ClientMessage, 'id' | 'timestamp'>) => void;
  showToast?: (message: string) => void;
}

export interface UseInpaintingReturn extends InpaintingState {
  setTool: (tool: 'rect' | 'lasso' | null) => void;
  setRegion: (region: MaskRegion | null) => void;
  setPrompt: (prompt: string) => void;
  /** Модель картинок (пакет 3 переезда AI); undefined — умолчание кампании. */
  model: string | undefined;
  setModel: (id: string | undefined) => void;
  /** Id сессии — для списка моделей в панели. */
  sessionId: string;
  generate: () => Promise<void>;
  accept: () => void;
  regenerate: () => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

const MIN_MASK_AREA = 32 * 32;
const MAX_MASK_RATIO = 0.8;

export function useInpainting(options: UseInpaintingOptions): UseInpaintingReturn {
  const { sessionId, participantId, pageId, mapWidth, mapHeight, mapScale = 1, send, showToast } = options;
  const [state, setState] = useState<InpaintingState>(INITIAL_STATE);
  // Выбор модели живёт отдельно от состояния: сброс окна его не теряет.
  const [model, setModel] = useState<string | undefined>(undefined);
  const modelRef = useRef<string | undefined>(undefined);
  modelRef.current = model;
  const { t } = useI18n();
  const abortRef = useRef<AbortController | null>(null);

  const setTool = useCallback((tool: 'rect' | 'lasso' | null) => {
    setState(s => ({ ...s, tool, region: null, previewUrl: null, error: null }));
  }, []);

  const setRegion = useCallback((region: MaskRegion | null) => {
    setState(s => ({ ...s, region, previewUrl: null, error: null }));
  }, []);

  const setPrompt = useCallback((prompt: string) => {
    setState(s => ({ ...s, prompt }));
  }, []);

  const doGenerate = useCallback(async () => {
    const currentState = state;
    if (!currentState.region || !currentState.prompt.trim()) return;

    // Validate mask size (in pixel space)
    const pixelRegionForValidation = worldToPixelRegion(currentState.region, mapScale);
    const maskArea = getMaskArea(pixelRegionForValidation);
    const mapArea = mapWidth * mapHeight;

    if (maskArea < MIN_MASK_AREA) {
      showToast?.(t('inpaint.tooSmall'));
      return;
    }
    if (maskArea / mapArea > MAX_MASK_RATIO) {
      showToast?.(t('inpaint.tooLarge'));
      return;
    }

    setState(s => ({ ...s, generating: true, error: null }));

    try {
      // Convert world coordinates to pixel coordinates, then clamp to map bounds
      const pixelRegion = worldToPixelRegion(currentState.region, mapScale);
      const clampedRegion = clampRegion(pixelRegion, mapWidth, mapHeight);

      const maskBlob = await exportMaskAsPng(clampedRegion, mapWidth, mapHeight, t);
      const formData = new FormData();
      formData.append('mode', 'inpaint');
      formData.append('mask', maskBlob, 'mask.png');
      formData.append('prompt', currentState.prompt);
      if (modelRef.current) formData.append('model', modelRef.current);
      formData.append('pageId', pageId);
      formData.append('width', String(mapWidth));
      formData.append('height', String(mapHeight));

      abortRef.current = new AbortController();

      const res = await fetch(
        `/api/generate-map?sessionId=${sessionId}&participantId=${participantId}`,
        { method: 'POST', body: formData, signal: abortRef.current.signal },
      );

      const data = await res.json() as { ok: boolean; url?: string; error?: string };
      if (data.ok && data.url) {
        setState(s => ({ ...s, previewUrl: data.url!, generating: false }));
      } else {
        setState(s => ({ ...s, generating: false, error: data.error ?? t('inpaint.errorGeneration') }));
        showToast?.(data.error ?? t('inpaint.errorGeneration'));
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setState(s => ({ ...s, generating: false }));
        return;
      }
      setState(s => ({ ...s, generating: false, error: t('inpaint.errorNetwork') }));
      showToast?.(t('inpaint.errorNetwork'));
    }
  }, [state, mapWidth, mapHeight, mapScale, pageId, sessionId, participantId, showToast, t]);

  const accept = useCallback(() => {
    if (!state.previewUrl) return;
    send({ type: 'map:inpaint-accept', url: state.previewUrl, pageId });
    setState(INITIAL_STATE);
  }, [state.previewUrl, pageId, send]);

  const regenerate = useCallback(async () => {
    await doGenerate();
  }, [doGenerate]);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(INITIAL_STATE);
  }, []);

  const reset = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setState(INITIAL_STATE);
  }, []);

  return useMemo(() => ({
    ...state,
    model,
    setModel,
    sessionId,
    setTool,
    setRegion,
    setPrompt,
    generate: doGenerate,
    accept,
    regenerate,
    cancel,
    reset,
  }), [
    state, setTool, setRegion, setPrompt,
    doGenerate, accept, regenerate, cancel, reset,
  ]);
}

/** Convert world-space region to image-pixel-space by dividing by mapScale */
export function worldToPixelRegion(region: MaskRegion, mapScale: number): MaskRegion {
  if (mapScale === 1) return region;
  if (region.type === 'rect') {
    return {
      type: 'rect',
      x: region.x / mapScale,
      y: region.y / mapScale,
      width: region.width / mapScale,
      height: region.height / mapScale,
    };
  }
  return {
    type: 'lasso',
    points: region.points.map(p => ({ x: p.x / mapScale, y: p.y / mapScale })),
  };
}

/** Clamp region coordinates to map boundaries */
export function clampRegion(region: MaskRegion, mapW: number, mapH: number): MaskRegion {
  if (region.type === 'rect') {
    const x = Math.max(0, Math.min(region.x, mapW));
    const y = Math.max(0, Math.min(region.y, mapH));
    const w = Math.min(region.width, mapW - x);
    const h = Math.min(region.height, mapH - y);
    return { type: 'rect', x, y, width: w, height: h };
  }
  return {
    type: 'lasso',
    points: region.points.map(p => ({
      x: Math.max(0, Math.min(p.x, mapW)),
      y: Math.max(0, Math.min(p.y, mapH)),
    })),
  };
}
